// Infinite scroll for data tables and other scroll containers.
//
// The instance listens to the scroll event of one container. The trigger uses a
// threshold instead of the exact end position. After every page the instance
// checks the container again, until the container can scroll or the API reports
// no next link.

const DEFAULT_THRESHOLD_PX = 150;
const MAX_AUTO_LOADS = 5;

class InfiniteTableScroll {
    constructor(component, state, registry) {
        this.component = component;
        this.state = state;
        this.registry = registry;
        this.element = null;
        this.handler = null;
        this.threshold = DEFAULT_THRESHOLD_PX;
        this.loading = false;
        this.pumping = false;
    }

    // Load page one. Resolves with this instance, so that the caller can attach
    // the scroll listener.
    load(endpoint) {
        const pagination = this.component.pagination;
        if (pagination) {
            pagination[this.state] = [];
        }
        this.component.dataTableLoading = true;

        return this.component.$store.dispatch('fetch', { endpoint: endpoint, state: this.state })
            .then((response) => {
                if (response && pagination) {
                    pagination[this.state] = Array.isArray(response.result) ? response.result : [];
                    pagination.total = response.total;
                }
                return this;
            })
            .catch((error) => {
                console.error('[infiniteTableScroll] Init failed for state', this.state, error);
                return this;
            })
            .finally(() => {
                this.component.dataTableLoading = false;
            });
    }

    addEventListener(selector, threshold = DEFAULT_THRESHOLD_PX) {
        const element = this.resolveElement(selector);
        if (!element) {
            console.warn('[infiniteTableScroll] No scroll container found for', selector);
            return;
        }

        const running = this.registry.get(element);
        if (running && running !== this) {
            running.detach();
        }
        if (this.element && this.element !== element) {
            this.detach();
        }

        this.element = element;
        this.threshold = threshold;
        element.style.overflowY = 'scroll';
        this.handler = () => {
            if (this.isNearEnd()) {
                this.pump();
            }
        };
        element.addEventListener('scroll', this.handler);
        this.registry.set(element, this);

        this.component.$nextTick(() => this.pump());
    }

    removeEventListener(selector) {
        if (this.element && (!selector || this.element.matches(selector))) {
            this.detach();
        }
    }

    detach() {
        if (this.element && this.handler) {
            this.element.removeEventListener('scroll', this.handler);
            this.registry.delete(this.element);
        }
        this.element = null;
        this.handler = null;
    }

    // Prefer the element inside this component. Fall back to the whole document.
    resolveElement(selector) {
        const root = this.component ? this.component.$el : null;
        if (root && typeof root.querySelector === 'function') {
            const scoped = root.querySelector(selector);
            if (scoped) {
                return scoped;
            }
        }
        return document.querySelector(selector);
    }

    isNearEnd() {
        const element = this.element;
        if (!element) {
            return false;
        }
        const distance = element.scrollHeight - (element.clientHeight + element.scrollTop);
        return distance <= this.threshold;
    }

    hasNext() {
        const entry = this.component.$store.state.main[this.state];
        return Boolean(entry && entry._links && entry._links.next && entry._links.next.href);
    }

    // Load pages while the container is near its end. A container without
    // overflow gets more pages too, so that the user can scroll at all.
    async pump() {
        if (this.pumping) {
            return;
        }
        this.pumping = true;

        try {
            let counter = 0;
            while (counter < MAX_AUTO_LOADS && this.hasNext() && this.isNearEnd()) {
                counter += 1;
                const loaded = await this.loadNext();
                if (!loaded) {
                    return;
                }
            }
        } finally {
            this.pumping = false;
        }
    }

    async loadNext() {
        if (this.loading || !this.hasNext()) {
            return false;
        }

        const endpoint = this.component.$store.state.main[this.state]._links.next.href;
        this.loading = true;
        this.component.dataTableLoading = true;

        try {
            const response = await this.component.$store.dispatch('fetch', { endpoint: endpoint, state: this.state });
            if (!response) {
                return false;
            }
            this.syncStore(response.result);
            return true;
        } catch (error) {
            console.error('[infiniteTableScroll] Load failed for state', this.state, error);
            return false;
        } finally {
            this.loading = false;
            this.component.dataTableLoading = false;
        }
    }

    scroll() {
        return this.pump();
    }

    syncStore(data) {
        if (!Array.isArray(data)) {
            data = Object.values(data);
        }
        const pagination = this.component.pagination;
        if (!pagination[this.state]) {
            pagination[this.state] = [];
        }
        pagination[this.state] = [...pagination[this.state], ...data];

        const current = this.component.$store.state.main[this.state] || {};
        this.component.$store.commit('set', {
            state: this.state,
            data: {
                ...current,
                result: pagination[this.state],
            },
        });
    }
}

class InfiniteTableScrollService {
    constructor() {
        // One scroll listener per element. The key is the scroll container.
        this.instances = new Map();
    }

    init(component, endpoint, state) {
        const instance = new InfiniteTableScroll(component, state, this.instances);
        return instance.load(endpoint);
    }

    removeEventListener(selector) {
        const element = document.querySelector(selector);
        if (!element) {
            return;
        }
        const instance = this.instances.get(element);
        if (instance) {
            instance.detach();
        }
    }

    // Remove every listener of one component. Used on destroy.
    detachAll(component) {
        this.instances.forEach((instance, element) => {
            if (instance.component === component) {
                instance.detach();
                this.instances.delete(element);
            }
        });
    }
}

export default new InfiniteTableScrollService();
